/**
 * In-portal purchase page — /portal/pricing
 * Reuses the rate/bracket data from /api/portal/billing/pricing and
 * lets the authenticated user buy credits via Razorpay (or demo mode).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Info, CheckCircle2, Loader2, Zap } from 'lucide-react';
import { billingApi, openRazorpayCheckout } from '../../services/billing';
import { formatMoney } from '../../lib/geo';
import { toast } from 'sonner';

const PortalPricing = () => {
  const [data, setData] = useState(null);
  const [customQty, setCustomQty] = useState(500);
  const [quote, setQuote] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [completedInvoice, setCompletedInvoice] = useState(null);
  // Auto top-up opt-in state. Persists across pack/custom purchases on
  // this page so a single tick + threshold setup applies to whichever
  // CTA the admin actually clicks. Defaults match the backend's
  // permissive defaults (refill 500 cr when balance drops below 50).
  const [autoTopupEnabled, setAutoTopupEnabled] = useState(false);
  const [autoTopupThreshold, setAutoTopupThreshold] = useState(50);
  const [autoTopupCredits, setAutoTopupCredits] = useState(500);
  // Sticky banner for createOrder/verify failures so the user is never
  // left wondering why nothing happened. Toasts on this page are easy to
  // miss when the user has already scrolled to a pack.
  const [lastError, setLastError] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    billingApi.pricing().then(({ data }) => setData(data)).catch(() => toast.error('Failed to load pricing'));
    const pref = params.get('credits');
    if (pref) setCustomQty(Number(pref));
  }, [params]);

  useEffect(() => {
    if (!data) return;
    const id = setTimeout(() => {
      billingApi.quote(Math.max(1, Number(customQty) || 1), data.currency)
        .then(({ data }) => setQuote(data)).catch(() => {});
    }, 200);
    return () => clearTimeout(id);
  }, [customQty, data]);

  const handleBuy = async (credits, pack_id = null) => {
    if (processing) return;
    setProcessing(true);
    try {
      const { data: order } = await billingApi.createOrder({
        credits: Number(credits),
        pack_id,
        // The platform is USD-only — without this, the backend falls
        // back to wallet.currency which defaults to INR, and the
        // Razorpay modal then shows ₹ even though the page shows $.
        currency: data.currency || 'USD',
      });
      await openRazorpayCheckout({
        ...order, credits,
        onSuccess: async (resp) => {
          try {
            const { data: v } = await billingApi.verifyOrder({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              credits: Number(credits),
              pack_id,
              enable_auto_topup: !!autoTopupEnabled,
              auto_topup_threshold: autoTopupEnabled ? Math.max(1, Number(autoTopupThreshold) || 50) : undefined,
              auto_topup_credits: autoTopupEnabled ? Math.max(1, Number(autoTopupCredits) || 500) : undefined,
            });
            setCompletedInvoice(v.invoice);
            window.dispatchEvent(new CustomEvent('credits:changed'));
            toast.success(`${credits} credits added — invoice ${v.invoice.invoice_number}`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Payment verification failed');
          } finally { setProcessing(false); }
        },
        onDismiss: () => setProcessing(false),
        // Surface Razorpay's actual error code/description instead of
        // the opaque "Something went wrong" the checkout shows by
        // default. Most common causes seen in the wild:
        //   BAD_REQUEST_ERROR / international_transaction_not_allowed
        //     → Razorpay account doesn't have International Payments
        //       activated (separate KYC step in dashboard).
        //   BAD_REQUEST_ERROR / amount or currency mismatch
        //     → server order != checkout payload (should never happen now).
        //   GATEWAY_ERROR / payment_failed
        //     → card declined / 3DS failed.
        onError: async (err) => {
          const code = err?.code || 'UNKNOWN';
          const desc = err?.description || 'Payment failed';
          const step = err?.step || '';
          const reason = err?.reason || '';
          toast.error(`Razorpay · ${code}: ${desc}${reason ? ` (${reason})` : ''}`,
            { duration: 8000 });
          // Async-fire-and-forget log to backend so support can grep for
          // recurring failure modes per-customer.
          try {
            await billingApi.logCheckoutError?.({ code, desc, step, reason,
              order_id: order.order?.id, currency: order.order?.currency });
          } catch { /* non-fatal */ }
          setProcessing(false);
        },
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not start payment';
      // Persistent + loud — toast.error alone is too easy to miss when the
      // user has already scrolled and clicked Buy. Show an inline banner
      // pinned to the top of the page until the user dismisses or retries.
      setLastError(detail);
      toast.error(detail, { duration: 10000 });
      setProcessing(false);
    }
  };

  const downloadInvoice = async (inv) => {
    const { data: blob } = await billingApi.invoicePdf(inv.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="p-8 text-nexus-text-tertiary">Loading pricing…</div>;

  // Production gating — if the merchant account doesn't have International
  // (USD) payments activated, /portal/billing/orders/create returns HTTP 400
  // and the Razorpay popup never opens. Surface that LOUDLY here so the
  // admin doesn't sit and tap Buy with no feedback.
  const intlBlocked = data.intl_enabled === false;

  return (
    <div className="space-y-6" data-testid="portal-pricing">
      {(intlBlocked || lastError) && (
        <div className="rounded-xl p-4 flex items-start gap-3"
          style={{
            background: 'rgba(255,80,80,0.06)',
            border: '1px solid rgba(255,80,80,0.4)',
            color: '#FF8E8E',
          }}
          data-testid="portal-pricing-error-banner">
          <Info size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-heading text-[13px] font-semibold">
              {intlBlocked ? 'Card payments are temporarily unavailable' : 'Payment could not start'}
            </div>
            <div className="text-[11.5px] mt-0.5 opacity-90">
              {intlBlocked
                ? 'International (USD) payments are disabled on this deployment. Please contact sales@cognara.ai or set RAZORPAY_INTL_ENABLED=true on the server.'
                : lastError}
            </div>
          </div>
          {lastError && !intlBlocked && (
            <button onClick={() => setLastError(null)}
              className="text-[11px] opacity-70 hover:opacity-100"
              data-testid="portal-pricing-dismiss-error">Dismiss</button>
          )}
        </div>
      )}
      <div>
        <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>BUY CREDITS</div>
        <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1">Top up your wallet</h1>
        <p className="text-[13px] text-nexus-text-secondary mt-2">
          Current balance: <b>{data.wallet.credit_balance.toFixed(1)} credits</b> ·
          Lifetime purchased: <b>{data.wallet.lifetime_credits_purchased.toLocaleString()}</b> ·
          Current rate: <b>{formatMoney(data.rates[data.wallet.bracket], data.currency)}/credit</b>
        </p>
      </div>

      {/* Auto top-up opt-in. Toggled on here to enable a saved-card
          recurring charge after the next successful purchase. The user
          can revisit and tweak threshold / refill amount any time from
          Settings → Auto top-up. */}
      <div className="rounded-xl p-5"
        style={{
          background: autoTopupEnabled ? 'rgba(16,185,129,0.06)' : 'var(--layer-1)',
          border: autoTopupEnabled ? '1px solid rgba(16,185,129,0.4)' : '1px solid var(--hairline)',
        }}
        data-testid="portal-auto-topup-optin">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={autoTopupEnabled}
            onChange={(e) => setAutoTopupEnabled(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#10B981]"
            data-testid="portal-auto-topup-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Zap size={14} style={{ color: autoTopupEnabled ? '#10B981' : '#F26B1F' }} />
              <span className="font-heading text-[14px] font-semibold text-nexus-text-primary">
                Enable Auto Top-Up after this purchase
              </span>
            </div>
            <p className="text-[11.5px] text-nexus-text-tertiary mt-1 leading-relaxed">
              When your wallet drops below the threshold, Cognara will automatically charge
              the card you used today and refill the wallet. You can disable or change this any time from Settings → Auto top-up.
            </p>
            {autoTopupEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-nexus-text-tertiary">Refill when balance drops below</div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number" min="1"
                      value={autoTopupThreshold}
                      onChange={(e) => setAutoTopupThreshold(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 rounded-lg text-[13px] font-medium outline-none focus:ring-2 text-nexus-text-primary"
                      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                      data-testid="portal-auto-topup-threshold"
                    />
                    <span className="text-[11px] text-nexus-text-tertiary">credits</span>
                  </div>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-nexus-text-tertiary">Refill with</div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number" min="1"
                      value={autoTopupCredits}
                      onChange={(e) => setAutoTopupCredits(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 rounded-lg text-[13px] font-medium outline-none focus:ring-2 text-nexus-text-primary"
                      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                      data-testid="portal-auto-topup-credits"
                    />
                    <span className="text-[11px] text-nexus-text-tertiary">credits</span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Packs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.packs.filter(p => p.paid).map((p) => {
          const isRec = p.recommended;
          return (
            <div key={p.id}
              className="relative rounded-xl p-5 flex flex-col"
              style={{
                background: isRec ? '#0A1B2E' : 'var(--layer-1)',
                border: isRec ? '1px solid #0A1B2E' : '1px solid var(--hairline)',
                color: isRec ? '#fff' : 'var(--nexus-text-primary)',
              }}
              data-testid={`portal-pack-${p.id}`}>
              {isRec && (
                <span className="absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wider"
                  style={{ background: '#F26B1F', color: '#fff' }}>RECOMMENDED</span>
              )}
              <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-heading text-3xl font-semibold">{p.credits.toLocaleString()}</span>
                <span className="text-[11px]" style={{ color: isRec ? '#A8B0C0' : 'var(--nexus-text-tertiary)' }}>credits</span>
              </div>
              <div className="mt-1 font-heading text-xl font-semibold">{formatMoney(p.price.total, data.currency)}</div>
              <button onClick={() => handleBuy(p.credits, p.id)} disabled={processing || intlBlocked}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isRec ? '#F26B1F' : (isRec ? '#fff' : '#0A1B2E'),
                  color: '#fff',
                }}
                data-testid={`portal-buy-${p.id}`}>
                {processing ? <Loader2 size={13} className="animate-spin" /> : intlBlocked ? <>Unavailable</> : <>Buy <ArrowRight size={12} /></>}
              </button>
            </div>
          );
        })}
      </div>

      {/* Custom quantity */}
      <div className="rounded-xl p-6" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
        <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>CUSTOM AMOUNT</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-4 items-end">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Credits</div>
            <input type="number" min="1" value={customQty} onChange={(e) => setCustomQty(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-[15px] font-medium outline-none focus:ring-2 text-nexus-text-primary"
              style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
              data-testid="portal-custom-qty" />
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Total</div>
            <div className="font-heading text-2xl font-semibold text-nexus-text-primary">
              {quote ? formatMoney(quote.total, data.currency) : '—'}
            </div>
          </div>
          <button onClick={() => handleBuy(customQty)} disabled={processing || !quote || intlBlocked}
            className="px-5 py-2.5 rounded-lg text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#F26B1F' }} data-testid="portal-custom-buy">
            {processing ? 'Processing…' : intlBlocked ? 'Unavailable' : 'Buy now'}
          </button>
        </div>
        {quote && quote.breakdown?.length > 1 && (
          <div className="mt-3 text-[11px] text-nexus-text-tertiary">
            Blended price: {quote.breakdown.map(b => `${b.credits}×${formatMoney(b.rate, data.currency)}`).join(' + ')}
          </div>
        )}
      </div>

      {/* Per-engine bracket comparison — what 1k/5k/10k credits cost on each engine */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="portal-model-comparison-card">
        <div className="px-5 pt-5 pb-2">
          <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>SIZE YOUR WALLET</div>
          <h3 className="font-heading text-lg font-semibold text-nexus-text-primary mt-1">Per-engine bracket comparison</h3>
          <p className="text-[11px] text-nexus-text-tertiary mt-1">
            What a fresh purchase costs across engines at your <b>pool lifetime = {data.pool_lifetime?.toLocaleString?.() || 0}</b> (volume-discounted).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="portal-model-comparison-table">
            <thead>
              <tr style={{ background: 'var(--layer-0)', borderTop: '1px solid var(--hairline)' }}>
                <th className="text-left px-5 py-2 font-semibold text-[10px] uppercase tracking-wider text-nexus-text-tertiary" style={{ width: '24%' }}>Credits</th>
                <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                  <div>Pro 4.6</div>
                  <div className="text-[9px] font-normal text-nexus-text-tertiary opacity-70">1.0× · balanced</div>
                </th>
                <th className="text-right px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                  <div>Ultra 4.7</div>
                  <div className="text-[9px] font-normal text-nexus-text-tertiary opacity-70">1.2× · premium</div>
                </th>
                <th className="text-right px-5 py-2 font-semibold text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                  <div>Apex 4.8</div>
                  <div className="text-[9px] font-normal text-nexus-text-tertiary opacity-70">1.5× · max reasoning</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[1000, 5000, 10000].map((qty) => {
                const calc = (m) => {
                  const rates = (data.rates_by_model || {})[m];
                  if (!rates) return null;
                  const ranges = [
                    { from: 0,     to: 1000,     key: '0-1000' },
                    { from: 1000,  to: 5000,     key: '1001-5000' },
                    { from: 5000,  to: 10000,    key: '5001-10000' },
                    { from: 10000, to: Infinity, key: '10001+' },
                  ];
                  // Slice from the user's CURRENT pool lifetime so the
                  // comparison reflects the rate they will actually pay.
                  let cursor = Number(data.pool_lifetime || 0);
                  let remaining = qty, total = 0;
                  for (const r of ranges) {
                    if (remaining <= 0) break;
                    if (r.to <= cursor) continue;
                    const cap = r.to - Math.max(r.from, cursor);
                    const take = Math.min(remaining, cap);
                    if (take <= 0) continue;
                    total += take * (rates[r.key] || 0);
                    remaining -= take;
                    cursor += take;
                  }
                  return total;
                };
                const cells = [
                  { m: 'claude-opus-4-6', v: calc('claude-opus-4-6') },
                  { m: 'claude-opus-4-7', v: calc('claude-opus-4-7') },
                  { m: 'claude-opus-4-8', v: calc('claude-opus-4-8') },
                ];
                return (
                  <tr key={qty} className="border-t" style={{ borderColor: 'var(--hairline)' }} data-testid={`portal-comparison-row-${qty}`}>
                    <td className="px-5 py-3">
                      <div className="font-heading text-base font-semibold text-nexus-text-primary">{qty.toLocaleString()}</div>
                      <div className="text-[10px] text-nexus-text-tertiary">credits</div>
                    </td>
                    {cells.map((c, idx) => (
                      <td key={c.m}
                        className={`py-3 text-right font-mono ${idx === cells.length - 1 ? 'px-5' : 'px-3'}`}
                        data-testid={`portal-cell-${qty}-${c.m}`}>
                        <div className="font-semibold text-[13px] text-nexus-text-primary">
                          {c.v == null ? '—' : formatMoney(c.v, data.currency)}
                        </div>
                        <div className="text-[9.5px] text-nexus-text-tertiary opacity-70">
                          {c.v == null ? '' : `${formatMoney(c.v / qty, data.currency)}/cr`}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 text-[10.5px] flex items-start gap-1.5 border-t text-nexus-text-tertiary" style={{ background: 'var(--layer-0)', borderColor: 'var(--hairline)' }}>
          <Info size={11} className="mt-0.5 flex-shrink-0" />
          <span>Volume brackets stack automatically — buying 5,000 credits pays the 0–1,000 rate on the first thousand, then the 1,001–5,000 rate on the rest. Your pool lifetime never resets, so each purchase pushes your team further down the curve.</span>
        </div>
      </div>

      {/* Rate card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>VOLUME BRACKETS</div>
            <div className="text-[10px] text-nexus-text-tertiary">USD per credit</div>
          </div>
          <table className="w-full text-[12px]" data-testid="portal-volume-brackets">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                <th className="text-left font-semibold py-1.5">Lifetime credits</th>
                <th className="text-right font-semibold py-1.5">Pro 4.6</th>
                <th className="text-right font-semibold py-1.5">Ultra 4.7</th>
                <th className="text-right font-semibold py-1.5">Apex 4.8</th>
              </tr>
            </thead>
            <tbody>
              {data.brackets.map((b) => {
                const r46 = (data.rates_by_model?.['claude-opus-4-6'] || data.rates || {})[b.label];
                const r47 = (data.rates_by_model?.['claude-opus-4-7'] || {})[b.label];
                const r48 = (data.rates_by_model?.['claude-opus-4-8'] || {})[b.label];
                const isCurrent = b.label === data.wallet.bracket;
                return (
                  <tr key={b.label}
                    className="border-t"
                    style={{ borderColor: 'var(--hairline)', background: isCurrent ? 'rgba(242,107,31,0.06)' : 'transparent' }}
                    data-testid={`portal-bracket-${b.label}`}>
                    <td className="py-2 text-nexus-text-secondary">
                      {b.max ? `${b.min.toLocaleString()} – ${b.max.toLocaleString()}` : `${b.min.toLocaleString()}+`}
                      {isCurrent && (
                        <span className="ml-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(242,107,31,0.18)', color: '#F26B1F' }}>You</span>
                      )}
                    </td>
                    <td className="py-2 text-right font-semibold text-nexus-text-primary">{r46 != null ? formatMoney(r46, data.currency) : '—'}</td>
                    <td className="py-2 text-right font-semibold text-nexus-text-primary">{r47 != null ? formatMoney(r47, data.currency) : '—'}</td>
                    <td className="py-2 text-right font-semibold text-nexus-text-primary">{r48 != null ? formatMoney(r48, data.currency) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-[10.5px] text-nexus-text-tertiary leading-relaxed">
            Bracket is set by your lifetime credits purchased and never resets upward. Each engine has its own rate card — your bracket applies to whichever engine you top up for.
          </p>
        </div>
        <div id="model-cost" className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
          <div className="text-[10px] font-semibold tracking-wider uppercase mb-2" style={{ color: '#F26B1F' }}>MODEL CREDIT COST</div>
          <p className="text-[12px] text-nexus-text-secondary leading-relaxed mb-3">
            Each chat call deducts <b>credits = ⌈total_tokens ÷ 1,000⌉ × multiplier</b>. One credit equals 1,000 tokens
            on the base engine (Cognara Engine · Pro 4.6). Heavier engines reason longer, so they cost a multiple of the base rate.
          </p>
          <table className="w-full text-[12px] mb-3">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                <th className="text-left font-semibold py-1">Engine</th>
                <th className="text-right font-semibold py-1">Multiplier</th>
                <th className="text-right font-semibold py-1">~Cost / 2k tokens</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.multipliers).map(([m, mult]) => {
                const baseCr = 2.0; // 2000 tokens = 2 base credits
                const callCr = (baseCr * mult).toFixed(1);
                const moneyPerCr = data.rates[data.wallet.bracket];
                const callMoney = formatMoney(callCr * moneyPerCr, data.currency);
                const _tier = m === 'claude-opus-4-8' ? 'Apex 4.8'
                  : m === 'claude-opus-4-7' ? 'Ultra 4.7'
                  : m === 'claude-opus-4-6' ? 'Pro 4.6'
                  : m.replace('claude-', '').replace('-4-', ' 4.');
                const label = `Cognara Engine · ${_tier}`;
                return (
                  <tr key={m} className="border-t" style={{ borderColor: 'var(--hairline)' }}>
                    <td className="py-2 text-nexus-text-secondary">{label}</td>
                    <td className="py-2 text-right font-mono" style={{ color: '#F26B1F' }}>{mult.toFixed(1)}×</td>
                    <td className="py-2 text-right text-nexus-text-primary">
                      {callCr} cr <span className="text-[10px] text-nexus-text-tertiary">({callMoney})</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="rounded-lg p-2.5 text-[11px] leading-relaxed text-nexus-text-secondary"
            style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}>
            <div className="font-semibold text-nexus-text-primary mb-1">Worked example</div>
            A 4,500-token Cognara Engine · Ultra 4.7 call → ⌈4500/1000⌉ = 5 base credits × 1.2× = <b>6 credits</b>
            ≈ {formatMoney(6 * data.rates[data.wallet.bracket], data.currency)} at your current bracket rate.
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-nexus-text-tertiary">
            <Info size={11} className="mt-0.5 flex-shrink-0" />
            <span>Token totals include both input (your prompts + RAG context) and output (model response). Tool results count as output.</span>
          </div>
        </div>
      </div>

      {/* Payment success */}
      {completedInvoice && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setCompletedInvoice(null)}>
          <div className="w-full max-w-md rounded-2xl p-6 text-center"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
              style={{ background: 'rgba(110,231,183,0.15)' }}>
              <CheckCircle2 size={28} style={{ color: '#10B981' }} />
            </div>
            <h3 className="font-heading text-xl font-semibold text-nexus-text-primary">Payment received</h3>
            <p className="text-[13px] text-nexus-text-secondary mt-1">
              {completedInvoice.credits_purchased} credits added · Invoice {completedInvoice.invoice_number}
            </p>
            <div className="mt-5 flex gap-2 justify-center">
              <button onClick={() => downloadInvoice(completedInvoice)}
                className="px-4 py-2 rounded-lg text-[12px] font-semibold"
                style={{ background: '#0A1B2E', color: '#fff' }}
                data-testid="download-invoice-btn">
                Download invoice
              </button>
              <button onClick={() => { setCompletedInvoice(null); navigate('/portal/chat'); }}
                className="px-4 py-2 rounded-lg text-[12px] font-semibold"
                style={{ background: '#F26B1F', color: '#fff' }}
                data-testid="go-to-chat-btn">
                Start chatting
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PortalPricing;
